import {
  TextManager,
  formatNumberVariableWithUnicode,
} from "Components/TextContext";
import {
  AkaHealthProduct,
  AkaHealthWithoutSigningProduct,
} from "Store/interfaces/products/checkout/lifeProducts/AkaHealthProduct";
import {
  HealthProduct,
  HealthWithoutSigningProduct,
} from "Store/interfaces/products/checkout/lifeProducts/HealthProduct";
import {
  BoatProduct,
  BoatType,
} from "Store/interfaces/products/checkout/riskProducts/BoatProduct";
import { hasDefinedProperties, isDefined } from "Utils/functions";
import { formatDateString, getAge, humanReadableDate } from "./dateUtils";

import { VALUE_TYPE } from "Components/NumberValue";
import { Label } from "Components/TextContext/commonComponentEnums";
import { formatToLocaleString } from "Formatting/formatNumber";
import { Cart } from "Store/cart/cartInterfaces";
import { PostalAddressDto } from "Store/interfaces/PostalAddressDto";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { InsuranceProduct } from "Store/interfaces/products/checkout";
import { AkaCriticalIllnessProduct } from "Store/interfaces/products/checkout/lifeProducts/AkaCriticalIllnessProduct";
import { AkaFamilyAccidentProduct } from "Store/interfaces/products/checkout/lifeProducts/AkaFamilyAccidentProduct";
import { AkaGroupLifeProduct } from "Store/interfaces/products/checkout/lifeProducts/AkaGroupLifeProduct";
import { ChildHealthProduct } from "Store/interfaces/products/checkout/lifeProducts/ChildHealthProduct";
import { ChildProduct } from "Store/interfaces/products/checkout/lifeProducts/ChildProduct";
import { CriticalIllnessProduct } from "Store/interfaces/products/checkout/lifeProducts/CriticalIllnessProduct";
import { DeathProduct } from "Store/interfaces/products/checkout/lifeProducts/DeathProduct";
import { DisabilityProduct } from "Store/interfaces/products/checkout/lifeProducts/DisabilityProduct";
import { PregnantAndNewbornProduct } from "Store/interfaces/products/checkout/lifeProducts/PregnantAndNewbornProduct";
import { SopSicknessLeaveProduct } from "Store/interfaces/products/checkout/lifeProducts/SopSicknessLeaveProduct";
import { AccidentProduct } from "Store/interfaces/products/checkout/riskProducts/AccidentProduct";
import { AnimalProduct } from "Store/interfaces/products/checkout/riskProducts/AnimalProduct";
import { TrailerProduct } from "Store/interfaces/products/checkout/riskProducts/TrailerProduct";
import { ValuablesProduct } from "Store/interfaces/products/checkout/riskProducts/ValuablesProduct";
import { AkaHouseContentsProduct } from "Store/interfaces/products/checkout/riskProducts/houseProducts/AkaHouseContentsProduct";
import { CabinContentsProduct } from "Store/interfaces/products/checkout/riskProducts/houseProducts/CabinContentsProduct";
import { CabinProduct } from "Store/interfaces/products/checkout/riskProducts/houseProducts/CabinProduct";
import { HouseContentsProduct } from "Store/interfaces/products/checkout/riskProducts/houseProducts/HouseContentsProduct";
import { HouseProduct } from "Store/interfaces/products/checkout/riskProducts/houseProducts/HouseProduct";
import { HuseierneHouseContentsProduct } from "Store/interfaces/products/checkout/riskProducts/houseProducts/HuseierneHouseContentsProduct";
import { BaseVehicleProduct } from "Store/interfaces/products/checkout/riskProducts/vehicleProducts/BaseVehicleProduct";
import { TractorProduct } from "Store/interfaces/products/checkout/riskProducts/vehicleProducts/TractorProduct";
import { VeteranAutoProduct } from "Store/interfaces/products/checkout/riskProducts/vehicleProducts/VeteranAutoProduct";
import { ChildCoverage } from "Store/interfaces/products/coverage";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { TextLanguage } from "Store/texts/interfaces";
import { getPriceFormatFromValue } from "../formatting";
import { capitalizeFirstLetter } from "./stringUtils";

interface DescriptionProps {
  product: InsuranceProduct;
  cart: Cart | null;
  productText: any;
  lang: TextLanguage;
  short?: boolean;
}

type LifeInsuranceProducts =
  | CriticalIllnessProduct
  | DeathProduct
  | DisabilityProduct
  | HealthProduct
  | HealthWithoutSigningProduct
  | SopSicknessLeaveProduct;

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getDescription({
  product,
  cart,
  productText,
  lang,
  short = false,
}: DescriptionProps): string {
  switch (product.name) {
    case INSURANCE_NAME_VALUE.AKA_FAMILY_ACCIDENT:
      if (short) {
        return "";
      }
      return getAkaFamilyAccidentDescription(
        product,
        productText.types.description,
        lang,
      );
    case INSURANCE_NAME_VALUE.AKA_GROUP_LIFE:
    case INSURANCE_NAME_VALUE.AKA_HEALTH:
    case INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS:
      return getMemberAndBirthDate(product);
    case INSURANCE_NAME_VALUE.AKA_HEALTH_WITHOUT_SIGNING:
      return getMemberAndBirthDate(product);
    case INSURANCE_NAME_VALUE.BOAT:
      return getBoatDescription(product, productText, lang, short);
    case INSURANCE_NAME_VALUE.CABIN:
    case INSURANCE_NAME_VALUE.CABIN_CONTENTS:
      return getCabinDescription(product);
    case INSURANCE_NAME_VALUE.DEATH:
    case INSURANCE_NAME_VALUE.DISABILITY_FLEX:
    case INSURANCE_NAME_VALUE.HEALTH:
    case INSURANCE_NAME_VALUE.HEALTH_WITHOUT_SIGNING:
    case INSURANCE_NAME_VALUE.CRITICAL_ILLNESS:
    case INSURANCE_NAME_VALUE.SOP_SICKNESS_LEAVE:
      return getPersonDescription(product, cart);
    case INSURANCE_NAME_VALUE.ACCIDENT:
      return getAccidentDescription(product, cart);
    case INSURANCE_NAME_VALUE.ANIMAL:
      return getAnimalDescription(product);
    case INSURANCE_NAME_VALUE.CHILD_HEALTH:
    case INSURANCE_NAME_VALUE.CHILD:
    case INSURANCE_NAME_VALUE.AKA_CHILD:
    case INSURANCE_NAME_VALUE.NEW_CHILD:
      return getChildDescription(product);
    case INSURANCE_NAME_VALUE.AKA_HOUSE_CONTENTS:
    case INSURANCE_NAME_VALUE.HUSEIERNE_HOUSE_CONTENTS:
    case INSURANCE_NAME_VALUE.HOUSE_CONTENTS:
    case INSURANCE_NAME_VALUE.HOUSE:
      return getHouseDescription(product);
    case INSURANCE_NAME_VALUE.RENTAL:
      if (product.streetAddress) {
        return product.streetAddress;
      }
      return `${product?.address?.zipCode ?? ""} ${
        capitalizeFirstLetter(product?.address?.city) ?? ""
      }`;
    case INSURANCE_NAME_VALUE.ELECTRIC_SCOOTER:
      return `${capitalizeFirstLetter(product.brand)} ${product.model}`;
    case INSURANCE_NAME_VALUE.MOPED:
    case INSURANCE_NAME_VALUE.MOTORCYCLE:
    case INSURANCE_NAME_VALUE.CAR:
    case INSURANCE_NAME_VALUE.TRAILER:
    case INSURANCE_NAME_VALUE.CAR_NORSK_ELBILFORENING:
      if (short) {
        return getShortLicensePlateString(product);
      }
      return `${capitalizeFirstLetter(product.brand)} ${
        product.model
      } ${getLicensePlateString(product)}`;
    case INSURANCE_NAME_VALUE.CARAVAN:
      if (short) {
        return `${product.brand} ${product.registrationYear}`;
      }
      return `${product.brand} ${product.model} ${product.registrationYear}`;
    case INSURANCE_NAME_VALUE.TRACTOR:
      return getTractorDescription(product, short);
    case INSURANCE_NAME_VALUE.VETERAN_AUTO:
      return getVeteranDescription(product, short);
    case INSURANCE_NAME_VALUE.AKA_STUDENT:
      return (
        product.country || productText.types.country[product.coverageGroup]
      );
    case INSURANCE_NAME_VALUE.VALUABLES:
      return getValuablesDescription(product);
    case INSURANCE_NAME_VALUE.AKA_TRAVEL:
    case INSURANCE_NAME_VALUE.TRAVEL:
      return productText.types.description[product.coverageGroup][lang];
    case INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN:
      return `${productText.types.insured[lang]} ${
        productText.types.description[product.coverageGroup][lang]
      }`;
    case INSURANCE_NAME_VALUE.MANUAL:
      return `${product.productIdentifier}, ${product.productDescription}`;
    default:
      return "";
  }
}

interface AdditionalDescriptionProps {
  product: InsuranceProduct;
  productText: any;
  lang: TextLanguage;
  isChildCoverageExtraHalfGActive: boolean;
}

export function getAdditionalDescription({
  product,
  productText,
  lang,
  isChildCoverageExtraHalfGActive = false,
}: AdditionalDescriptionProps): string {
  const { getText } = TextManager;
  switch (product.name) {
    case INSURANCE_NAME_VALUE.AKA_HOUSE_CONTENTS:
    case INSURANCE_NAME_VALUE.HUSEIERNE_HOUSE_CONTENTS:
    case INSURANCE_NAME_VALUE.HOUSE_CONTENTS:
    case INSURANCE_NAME_VALUE.VALUABLES:
      return `${
        productText.details.coverageAmount[lang] + ":"
      } ${getPriceFormatFromValue(product.coverageAmount, VALUE_TYPE.NOK)}`;
    case INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS:
    case INSURANCE_NAME_VALUE.AKA_GROUP_LIFE:
    case INSURANCE_NAME_VALUE.AKA_HEALTH:
    case INSURANCE_NAME_VALUE.AKA_HEALTH_WITHOUT_SIGNING:
      return getCoInsuredInfo(product);
    case INSURANCE_NAME_VALUE.SOP_SICKNESS_LEAVE:
      return product.dailyPayoutAmount
        ? `${
            productText.types.description.dailyPayoutAmount[lang]
          } ${formatToLocaleString(product.dailyPayoutAmount)}`
        : "";
    case INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN:
      const dateType = product.pregnant
        ? productText.details.dueDate[lang]
        : productText.details.birthDate[lang];
      return `${dateType} ${formatDateString(
        new Date(product.childBirthDate),
      )}`;
    case INSURANCE_NAME_VALUE.HOUSE:
      return product.priceSpecifications.deductible.code
        ? getText("insurancecard.house.description-deductible", {
            variables: {
              deductible: getPriceFormatFromValue(
                product.priceSpecifications.deductible.code,
                VALUE_TYPE.NOK,
              ),
            },
          })
        : "";
    case INSURANCE_NAME_VALUE.DISABILITY_FLEX:
      return `${productText.details.insuredSum[lang]}: ${formatToLocaleString(
        product.insuredSum,
      )} ${VALUE_TYPE.NOK}`;
    case INSURANCE_NAME_VALUE.DEATH:
      return `${productText.details.payment[lang]}: ${formatToLocaleString(
        product.insuredSum,
      )} ${VALUE_TYPE.NOK}`;
    case INSURANCE_NAME_VALUE.CRITICAL_ILLNESS:
      return `${
        productText.details.coverageAmount[lang]
      }: ${formatToLocaleString(product.insuredSum)} ${VALUE_TYPE.NOK}`;
    case INSURANCE_NAME_VALUE.ANIMAL:
      return getAdditionalAnimalDescription(product);
    case INSURANCE_NAME_VALUE.NEW_CHILD:
      if (isChildCoverageExtraHalfGActive) {
        return getAdditionalNewchildDescription(product);
      }
      return "";
    default:
      return "";
  }
}

interface IssueDateProps {
  readonly product: InsuranceProduct;
}

export function getIssueDate({ product }: IssueDateProps): string {
  const { getText } = TextManager;
  if (product.issueDate) {
    return getText("insurancecard.common.issuedate", {
      variables: { date: humanReadableDate(new Date(product.issueDate)) },
    });
  }
  return "";
}

export function getShortTermExpiryText(
  product: PregnantAndNewbornProduct,
): string {
  const { getText } = TextManager;
  const pluralForm = product.multipleChildren ? "many" : "one";

  if (product.pregnant) {
    if (product.multipleChildren) {
      return getText(
        "insurancecard.pregnantandnewborn.expirydatepregnantmultiple",
      );
    }
    return getText("insurancecard.pregnantandnewborn.expirydatepregnant");
  } else {
    return getText("insurancecard.pregnantandnewborn.expirydate", {
      plurals: pluralForm,
    });
  }
}

export const getAkaFamilyAccidentDescription = (
  product: AkaFamilyAccidentProduct,
  descriptionText: any,
  lang: string,
): string => {
  const deathCoverage = `${
    product.hasDeathCoverage ? descriptionText.deathCoverage[lang] : ""
  }`;
  const disabilityCoverage = `${
    product.hasDisabilityCoverage
      ? descriptionText.disabilityCoverage[lang]
      : ""
  }`;
  const professionalDisabilityCoverage = `${
    product.hasProfessionalDisabilityCoverage
      ? descriptionText.professionalDisabilityCoverage[lang]
      : ""
  }`;
  const firstComma =
    deathCoverage &&
    (disabilityCoverage || professionalDisabilityCoverage) &&
    ", ";
  const secondComma =
    disabilityCoverage && professionalDisabilityCoverage && ", ";
  return `${deathCoverage}${firstComma}${disabilityCoverage}${secondComma}${professionalDisabilityCoverage}`;
};

function getShortLicensePlateString(
  product: BaseVehicleProduct | TrailerProduct,
): string {
  const brandName = capitalizeFirstLetter(product.brand);
  return product.licensePlateNumber
    ? `${brandName} ${product.licensePlateNumber}`
    : `${brandName} ${product.model}`;
}

function getLicensePlateString(
  product: BaseVehicleProduct | TrailerProduct,
): string {
  return product.licensePlateNumber ? ` - ${product.licensePlateNumber}` : "";
}

function getTractorDescription(
  product: TractorProduct,
  short: boolean,
): string {
  if (product.licensePlateNumber) {
    if (short) {
      if (product.model.split(" ").length > 1) {
        return `${product.model.split(" ")[0]} ${
          product.licensePlateNumber ?? ""
        }`;
      }
      return `${product.model} ${product.licensePlateNumber ?? ""}`;
    }
    return `${product.model} ${getLicensePlateString(product)}`;
  }
  return `${product.model}`;
}

function getVeteranDescription(
  product: VeteranAutoProduct,
  short: boolean,
): string {
  if (short) {
    if (product.modelDescription.split(" ").length > 1) {
      return `${product.modelDescription.split(" ")[0]} ${product.modelYear} ${
        product.licensePlateNumber ?? ""
      }`;
    }
    return `${product.modelDescription} ${product.modelYear} ${
      product.licensePlateNumber ?? ""
    }`;
  }
  return `${product.modelDescription} ${
    product.modelYear
  } ${getLicensePlateString(product)}`;
}

export function getBoatDescription(
  product: BoatProduct,
  productText: any,
  lang: string,
  short: boolean,
): string {
  if (product.boatType === BoatType.SMALL_BOAT) {
    return productText.types.boatTypes[product.boatType][lang];
  }
  if (
    short &&
    product.brandAndModel &&
    product.brandAndModel.split(" ").length > 1
  ) {
    return `${product.brandAndModel.split(" ")[0]} ${product.modelYear}`;
  }
  return `${product.brandAndModel} ${product.modelYear}`;
}

const getValuablesDescription = (product: ValuablesProduct): string => {
  const [{ types }, lang] = useProductTexts(getFMSProductName(product.name));
  return `${types[product.valuableType][lang]}`;
};

const getPersonDescription = (
  product: LifeInsuranceProducts,
  cart: Cart | null,
): string => {
  const [{ details }, lang] = useProductTexts("child");
  let person = "Person";
  if (cart && cart.customer.firstName) {
    person = cart.customer.firstName;
  }
  return `${person} ${getAge(new Date(product.dateOfBirth))} ${
    details.year[lang]
  }`;
};

const getAccidentDescription = (
  product: AccidentProduct,
  cart: Cart | null,
): string => {
  const [{ details }, lang] = useProductTexts("child");
  let person = "Person";
  if (product.isInsuredOwner === false && product.firstName) {
    person = product.firstName;
  }
  if (cart && cart.customer.firstName && product.isInsuredOwner !== false) {
    person = cart.customer.firstName;
  }
  return `${person} ${getAge(new Date(product.dateOfBirth))} ${
    details.year[lang]
  }`;
};

const getChildDescription = (
  product: ChildProduct | ChildHealthProduct,
): string => {
  const [{ details }, lang] = useProductTexts("child");
  const person = product.childFirstName
    ? product.childFirstName
    : details.child[lang];
  return `${person} ${getAge(new Date(product.childDateOfBirth))} ${
    details.year[lang]
  }`;
};

const getMemberAndBirthDate = (
  product:
    | AkaCriticalIllnessProduct
    | AkaGroupLifeProduct
    | AkaHealthProduct
    | AkaHealthWithoutSigningProduct,
): string => {
  const [{ details }, lang] = useProductTexts(getFMSProductName(product.name));
  return product.memberApplicantPersonInformation?.dateOfBirth
    ? `
      ${details.bornMember[lang]} ${formatDateString(
        new Date(product.memberApplicantPersonInformation.dateOfBirth),
      )}`
    : "";
};

const getCoInsuredInfo = (
  product:
    | AkaCriticalIllnessProduct
    | AkaGroupLifeProduct
    | AkaHealthProduct
    | AkaHealthWithoutSigningProduct,
): string => {
  const [{ details }, lang] = useProductTexts(getFMSProductName(product.name));
  return product.coInsuredApplicantPersonInformation?.dateOfBirth
    ? `${details.bornCoInsured[lang]} ${formatDateString(
        new Date(product.coInsuredApplicantPersonInformation.dateOfBirth),
      )}`
    : "";
};

function getHouseDescription(
  product:
    | AkaHouseContentsProduct
    | HouseContentsProduct
    | HuseierneHouseContentsProduct
    | HouseProduct,
): string {
  const { getText } = TextManager;
  if ("realEstateInfo" in product && isDefined(product.address)) {
    const postalInfo = getPostalInfo(product.address);
    return isDefined(postalInfo)
      ? `${product.address.line1}, ${postalInfo}`
      : product.address.line1;
  }
  if (product.addressSameAsHomeAddress) {
    return getText(Label.REGISTERED_ADDRESS);
  }
  if (product.streetAddress) {
    return product.streetAddress;
  }
  return getPostalInfo(product.address) ?? "";
}

const getPostalInfo = (address?: PostalAddressDto): string | undefined =>
  isDefined(address) && hasDefinedProperties(address, ["zipCode", "city"])
    ? `${address.zipCode} ${capitalizeFirstLetter(address.city)}`
    : undefined;

export const getHouseContentsDeductible = (
  product: HouseContentsProduct,
): string => {
  const { getText } = TextManager;
  if (product.priceSpecifications.deductible.code) {
    return getText("insurancecard.housecontents.description-deductible", {
      variables: {
        deductible: getPriceFormatFromValue(
          product.priceSpecifications.deductible.code,
          VALUE_TYPE.NOK,
        ),
      },
    });
  }
  return "";
};

export const getDisabilityFlexSinglePayment = (
  product: DisabilityProduct,
  productText: any,
  lang: TextLanguage,
): string => {
  if (product.coverageAmountCapital) {
    return `${
      productText.details.coverageAmountCapital[lang]
    }: ${formatToLocaleString(product.coverageAmountCapital)} ${
      VALUE_TYPE.NOK
    }`;
  }
  return "";
};

function getCabinDescription(
  product: CabinProduct | CabinContentsProduct,
): string {
  if (product.streetAddress) {
    return product.streetAddress;
  }
  return `${product?.address?.zipCode ?? ""} ${product?.address?.city ?? ""}`;
}

const getAnimalDescription = (product: AnimalProduct): string => product.breed;

const getAdditionalAnimalDescription = (product: AnimalProduct): string => {
  const vetCoverage =
    isDefined(product.coverageAmountVet) && product.coverageAmountVet !== 0
      ? `Veterinærutgifter (${formatNumberVariableWithUnicode(
          product.coverageAmountVet,
        )} kr) `
      : "";

  const deathCoverage =
    product.coverageAmountDeath !== 0
      ? `Dødsfall (${formatNumberVariableWithUnicode(
          product.coverageAmountDeath,
        )} kr) `
      : "";

  return `${deathCoverage}\n${vetCoverage}`;
};

const getAdditionalNewchildDescription = (product: ChildProduct): string => {
  const { getText } = TextManager;
  switch (product.coverageGroup) {
    case ChildCoverage.EXTRA_HALF_G:
      return getText("insurancecard.newchild.description-half-g");
    case ChildCoverage.EXTRA:
      return getText("insurancecard.newchild.description-one-g");
    default:
      return "";
  }
};
